import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import {
  CARD_CONTENT_OVERLAY_TABLES,
  resolveUserCardSubject,
} from "@-/dev.improve.haniverse.lib.cards.card-content";
import {
  tableNameMatcher,
  useTableChangeVersion,
} from "@-/dev.improve.haniverse.lib.database.change";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import {
  RECENT_EXPOSURE_WINDOW_MS,
  type RecentExposures,
  stampExposure,
} from "#order-review-queue";

// The overlays are joined through `user_card`, so an overlay sync changes which subject a review
// maps to; `user_card_review` is the row that records the review itself.
const RECENT_EXPOSURES_TABLES = [
  "user_card_review",
  "user_card",
  ...CARD_CONTENT_OVERLAY_TABLES,
] as const;

export function useGetRecentExposuresQueryKey() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const withTableChangeVersion = useTableChangeVersion(tableNameMatcher(RECENT_EXPOSURES_TABLES));
  return () => withTableChangeVersion(["recentReviewExposures", { actorDb }] as const);
}

/**
 * Every subject reviewed within {@link RECENT_EXPOSURE_WINDOW_MS}, with its latest review time —
 * the scheduler's "what was just on screen" read (see `orderReviewQueue`).
 *
 * Bounded by the window, so it is O(reviews this sitting), not O(deck). Read through the
 * soft-delete-aware relational API: an undone review is tombstoned and stops counting as an
 * exposure. The cutoff is a fetch-time value, hence the direct read.
 *
 * Known limit: the window is measured against the clock at fetch time, but nothing invalidates the
 * cached entry when the window lapses without a write. Every rating writes `user_card_review`, so
 * the scheduler always re-reads on the next decision that matters.
 */
export const [useRecentExposuresQueryOptions, recentExposuresQueryFn] = createUseQueryOptions(
  useGetRecentExposuresQueryKey,
  async ({ queryKey: [_, { actorDb }] }): Promise<RecentExposures> => {
    const cutoffIso = new Date(Date.now() - RECENT_EXPOSURE_WINDOW_MS).toISOString();
    // oxlint-disable-next-line han-actor-db/no-raw-actor-db-in-query-fn -- the window cutoff is a fetch-time value, see above
    const rows = await actorDb(import.meta.location).query.userCardReview.findMany({
      columns: { reviewedAt: true },
      where: { reviewedAt: { gte: cutoffIso } },
      with: {
        userCard: {
          columns: { id: true },
          with: {
            userGraphemeMeaningCard: { columns: { grapheme: true } },
            userGraphemePronunciationCard: { columns: { grapheme: true } },
            userWordSentenceCard: { columns: { word: true } },
          },
        },
      },
    });

    const exposures = new Map<string, number>();
    for (const row of rows) {
      const reviewedAtMs = new Date(row.reviewedAt).getTime();
      if (Number.isNaN(reviewedAtMs)) {
        continue;
      }
      // The same subject the due scan resolves for this card.
      const subject = resolveUserCardSubject(row.userCard);
      if (subject === null) {
        continue;
      }
      stampExposure(exposures, subject.text, reviewedAtMs);
    }
    return exposures;
  },
);
